import { forwardRef } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import colors from 'components/utils/colors'
import IconLink from 'components/Shared/IconLink/Index'

const PADDING_HORIZ = 7
const PADDING_WITH_ICON = 25
const HEIGHT = 32
const BORDER_SIZE = 1

const StyledInputFieldWrapper = styled.div`
  width: ${props => props.width || '100%'};
  position: relative;
  display: flex;
  align-items: center;
`

const styleInputWithIcon = css`
  padding-right: ${PADDING_WITH_ICON}px;
`

const styleInputAsError = css`
  && {
    border-color: ${colors.alert};
  }
`

const StyledClearButton = styled(IconLink)`
  position: absolute;
  right: 0;
  padding: 10px;
  padding-right: 12px
`

const IconHolder = styled.div`
  position: absolute;
  pointer-events: none;
  right: 0;
  padding: 8px;
  padding-right: 8px
`

const StyledInputField = styled.input`
  display: flex;
  flex: 1;
  font-weight: 400;
  font-size: 14px;
  text-overflow: ellipsis;
  letter-spacing: -0.2px;
  line-height: 20px;
  height: ${HEIGHT - 2 * BORDER_SIZE}px;
  padding: 0 ${PADDING_HORIZ}px 0 ${PADDING_HORIZ}px;
  color: ${colors.secondary20};
  border-radius: 3px;
  border: ${BORDER_SIZE}px solid ${colors.secondary70};
  outline: none;
  &::placeholder {
    color: ${colors.secondary70};
  }
  &:focus, &:hover {
    border: ${BORDER_SIZE}px solid ${colors.primary40};
  }
  ${props => props.isError && styleInputAsError}
  ${props => (props.withClearIcon || props.withCustomIcon) && styleInputWithIcon}
`

const InputField = forwardRef(({
  value, showClear, className, width, maxLength, onFocus, onBlur, onKeyDown, onClick,
  onChange, onChangeRaw, placeholder, isError, disabled, iconComponent, dataTest,
}, ref) => {
  const onChange_ = (event) => {
    onChangeRaw?.(event)
    const value = event.target.value
    onChange(value)
  }

  const onClear = () => {
    onChange('')
  }

  const showClearIcon = value && showClear
  const withCustomIcon = Boolean(iconComponent)
  return (
    <StyledInputFieldWrapper className={className} width={width}>
      <StyledInputField
        ref={ref}
        maxLength={maxLength || 120}
        type='text'
        value={value}
        onChange={onChange_}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onClick={onClick}
        placeholder={placeholder}
        isError={isError}
        withClearIcon={showClearIcon}
        withCustomIcon={withCustomIcon}
        disabled={disabled}
        data-test={dataTest}
      />
      {showClearIcon && (
        <StyledClearButton
          iconName='icon-clear-search'
          onClick={onClear}
        />
      )}
      {withCustomIcon && (
        <IconHolder>
          {iconComponent}
        </IconHolder>
      )}
    </StyledInputFieldWrapper>
  )
})

InputField.displayName = 'InputField'

InputField.propTypes = {
  value: PropTypes.string,
  showClear: PropTypes.bool,
  width: PropTypes.string,
  maxLength: PropTypes.number,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onClick: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onChangeRaw: PropTypes.func,
  placeholder: PropTypes.string,
  isError: PropTypes.bool,
  disabled: PropTypes.bool,
  iconComponent: PropTypes.node,
  dataTest: PropTypes.string,
}

export default InputField
